import { t as trapFocus, r as removeTrapFocus } from './index-BIftRuJC.js';

class CartDrawerToggle extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      console.error('Cart drawer not found');
      return;
    }

    // If we're already open, close it
    if (document.body.classList.contains('cart-drawer-is-open')) {
      cartDrawer.close();
    } else {
      cartDrawer.open();
    }
  }
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawer = this;
    this.body = document.querySelector('body');
    this.activeClass = 'cart-drawer-is-open';
    this.cartIconBubble = document.querySelector('cart-icon-bubble');

    this.closeButtons = this.querySelectorAll('[data-cart-button-close]');
    this.closeButtons.forEach((btn) => btn.addEventListener('click', this.close.bind(this)));

    this.onBodyClick = this.handleBodyClick.bind(this);

    this.drawer.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    // Listen for cart updates in other components
    document.addEventListener('cart-updated', (event) => {
      if (event.detail) {
        this.renderContents(event.detail);
        this.open();
      }
    });
  }

  open() {
    this.body.classList.add(this.activeClass);
    this.drawer.focus();
    trapFocus({ container: this.drawer });
    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.body.classList.remove(this.activeClass);
    document.body.removeEventListener('click', this.onBodyClick);
    removeTrapFocus();
  }

  /**
   * Renders the cart contents based on the provided state
   * Updates cart items, footer, and cart count displays
   * @param {Object} parsedState - The cart state containing items and sections
   */
  renderContents(parsedState) {
    if (!parsedState) return;

    // Enable loading state
    this.enableLoading();

    // Determine cart count from available sources
    let cartCount;
    if (typeof parsedState.item_count === 'number') {
      cartCount = parsedState.item_count;
    } else if (parsedState.items?.length) {
      // Calculate count from line items if item_count not available
      cartCount = parsedState.items.reduce((total, item) => total + (item.quantity || 0), 0);
    } else {
      // Fallback: fetch cart data directly if count not available in state
      fetch(`${routes.cart_url}.js`)
        .then((response) => response.json())
        .then((cart) => {
          if (typeof cart.item_count === 'number') {
            this.updateAllCartCounts(cart.item_count);
          }
        })
        .catch((e) => console.error('[CartDrawer] Error fetching cart:', e));
    }

    // Update cart sections with new HTML
    this.getSectionsToRender().forEach((section) => {
      if (section?.selector) {
        const elements = document.querySelectorAll(section.selector);
        if (!elements.length) return;

        if (!parsedState.sections?.[section.section]) return;

        const newHTML = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );

        if (newHTML) {
          elements.forEach((element) => {
            element.innerHTML = newHTML;
          });
        }
      }
    });

    // Update cart count displays if available
    if (typeof cartCount === 'number') {
      this.updateAllCartCounts(cartCount);
    }

    this.toggleEmptyCart();

    // Re-bind the close drawer buttons after content update
    this.querySelectorAll('[data-cart-button-close]').forEach((btn) =>
      btn.addEventListener('click', this.close.bind(this))
    );

    // Disable loading state
    this.disableLoading();
  }

  /**
   * Toggle the empty state class based on line items
   */
  toggleEmptyCart() {
    this.currentItemCount = this.querySelectorAll('[data-line-item]').length;
    this.classList.toggle('cart-is-empty', this.currentItemCount === 0);
  }

  /**
   * Get all cart sections that need to be re-rendered on update
   * @returns {Array} Array of section objects with id, section name, and selector
   */
  getSectionsToRender() {
    const sections = [];

    // Main cart items section
    const mainCartItems = document.getElementById('main-cart-items');

    if (mainCartItems?.dataset?.id) {
      sections.push({
        id: 'main-cart-items',
        section: mainCartItems.dataset.id,
        selector: '.js-cart-item-contents',
      });
    }

    // Cart footer section
    const mainCartFooter = document.getElementById('main-cart-totals');

    if (mainCartFooter?.dataset?.id) {
      sections.push({
        id: 'main-cart-totals',
        section: mainCartFooter.dataset.id,
        selector: '.js-cart-footer-contents',
      });
    }

    // Cart icon bubble is always included
    sections.push({
      id: 'cart-icon-bubble',
      section: 'cart-icon-bubble',
      selector: '[data-cart-icon-bubble]',
    });

    return sections;
  }

  /**
   * Extract inner HTML from a section using provided selector
   * @param {string} html - The HTML string to parse
   * @param {string} selector - The selector to find within the HTML
   * @returns {string|undefined} The inner HTML of the matched element
   */
  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector(selector);
    return element?.innerHTML;
  }

  /**
   * Handle clicks outside the cart drawer to close it
   * @param {Event} event - The click event
   */
  handleBodyClick(event) {
    const target = event.target;
    if (target !== this.drawer && !target.closest('cart-drawer')) {
      this.close();
    }
  }

  /**
   * Update all cart count displays throughout the page
   * @param {number} count - The new cart count
   */
  updateAllCartCounts(count) {
    // Update cart-item-count custom elements
    const cartItemCounts = document.querySelectorAll('cart-item-count');
    cartItemCounts.forEach((counter) => {
      counter.textContent = count;
      counter.classList.toggle('hidden', count === 0);
    });

    // Update cart page count
    const cartPageCounts = document.querySelectorAll('[data-cart-count]');
    cartPageCounts.forEach((counter) => {
      counter.textContent = count;
      counter.classList.toggle('hidden', count === 0);
    });

    // Update cart icon bubble count
    if (this.cartIconBubble) {
      const cartIconBubbleCounts = this.cartIconBubble.querySelectorAll('.cart-icon-bubble__count');
      cartIconBubbleCounts.forEach((counter) => {
        counter.classList.toggle('hidden', count === 0);
        const cartCount = counter.querySelector('.cart-count');
        if (cartCount) {
          cartCount.textContent = count;
        }
      });
    }
  }

  /**
   * Enable loading state
   */
  enableLoading() {
    this.querySelector('cart-items')?.classList.add('cart-items-is-loading');
    const loaderElements = this.querySelectorAll('[data-cart-loader-active="show"]');
    const contentElements = this.querySelectorAll('[data-cart-loader-active="hidden"]');

    loaderElements.forEach((el) => {
      el.classList.remove('hidden');
      // Ensure loader is visible in cart footer
      if (el.closest('.cart-drawer__footer')) {
        el.style.display = 'block';
      }
    });

    contentElements.forEach((el) => {
      el.classList.add('hidden');
      // Ensure content is hidden in cart footer
      if (el.closest('.cart-drawer__footer')) {
        el.style.display = 'none';
      }
    });
  }

  /**
   * Disable loading state
   */
  disableLoading() {
    const loaderElements = this.querySelectorAll('[data-cart-loader-active="show"]');
    const contentElements = this.querySelectorAll('[data-cart-loader-active="hidden"]');

    loaderElements.forEach((el) => {
      el.classList.add('hidden');
      // Ensure loader is hidden in cart footer
      if (el.closest('.cart-drawer__footer')) {
        el.style.display = 'none';
      }
    });

    contentElements.forEach((el) => {
      el.classList.remove('hidden');
      // Ensure content is visible in cart footer
      if (el.closest('.cart-drawer__footer')) {
        el.style.display = 'block';
      }
    });

    this.querySelector('cart-items')?.classList.remove('cart-items-is-loading');
  }
}

customElements.define('cart-drawer', CartDrawer);
customElements.define('cart-drawer-toggle', CartDrawerToggle);
